import {
  IGenerateReportOperator,
  ReportNightEntry,
} from "../../../../api/types/reports/IGenerateReportJsonResponse";
import { reportTableTheme } from "../reportTableTheme";
import {
  getCompetitorCellBackground,
  getCompetitorCellDeltaColor,
} from "./getCompetitorCellBackground";
import { getPriceDeltaCellBackground } from "./getPriceDeltaCellBackground";

export interface ResolvedReportCell {
  price: number | null;
  delta: string;
  backgroundColor: string;
  deltaColor: string;
  priceColor: string;
  hasApiBackground: boolean;
}

export interface ResolveOperatorCellOptions {
  /** Tez (our) minimum price for selected nights; used when recalculating backgrounds. */
  tezMinPrice?: number | null;
  /**
   * When true (night filter applied), replace missing API backgrounds using
   * tez vs operator minimum price delta. API-provided colors are kept.
   */
  applyPriceDeltaBackground?: boolean;
}

function isNightEntry(value: ReportNightEntry | string): value is ReportNightEntry {
  return Array.isArray(value);
}

function parseNightEntry(entry: ReportNightEntry): ResolvedReportCell | null {
  if (entry.length === 0) {
    return null;
  }

  const delta = entry.length > 1 ? String(entry[1] ?? "") : "";
  const hasBackgroundField = entry.length > 2;
  const hasDeltaColorField = entry.length > 3;
  const hasPriceColorField = entry.length > 4;
  const hasApiBackground =
    hasBackgroundField &&
    entry[2] !== "" &&
    entry[2] !== null &&
    entry[2] !== undefined;
  const hasApiDeltaColor =
    hasDeltaColorField &&
    entry[3] !== "" &&
    entry[3] !== null &&
    entry[3] !== undefined;
  const hasApiPriceColor =
    hasPriceColorField &&
    entry[4] !== "" &&
    entry[4] !== null &&
    entry[4] !== undefined;

  let backgroundColor = hasApiBackground ? String(entry[2]) : "#FFFFFF";
  let deltaColor = hasApiDeltaColor ? String(entry[3]) : "#000000";
  const priceColor = hasApiPriceColor
    ? String(entry[4])
    : reportTableTheme.defaultPriceColor;

  if (!hasBackgroundField && delta) {
    backgroundColor = getCompetitorCellBackground(delta);
  }

  if (!hasDeltaColorField && delta) {
    deltaColor = getCompetitorCellDeltaColor(delta, deltaColor);
  }

  const rawPrice = entry[0];
  const hasPrice =
    rawPrice !== "" && rawPrice !== null && rawPrice !== undefined;

  if (!hasPrice) {
    if (!hasApiBackground) {
      return null;
    }

    return {
      price: null,
      delta,
      backgroundColor,
      deltaColor,
      priceColor,
      hasApiBackground,
    };
  }

  const price = typeof rawPrice === "number" ? rawPrice : Number(rawPrice);
  if (Number.isNaN(price)) {
    return null;
  }

  return {
    price,
    delta,
    backgroundColor,
    deltaColor,
    priceColor,
    hasApiBackground,
  };
}

function getOperatorNightsMap(
  operator: IGenerateReportOperator,
): Record<string, ReportNightEntry> | null {
  const { nights } = operator;
  if (!nights || !Array.isArray(nights)) {
    if (nights && typeof nights === "object") {
      return nights;
    }
    return null;
  }

  if (nights.length === 0) {
    return null;
  }

  const nightsArray = nights as Array<ReportNightEntry | string>;
  if (nightsArray.every((value) => typeof value === "string")) {
    return null;
  }

  const map: Record<string, ReportNightEntry> = {};
  nightsArray.forEach((value, index) => {
    if (isNightEntry(value)) {
      map[String(index)] = value;
    }
  });

  return Object.keys(map).length > 0 ? map : null;
}

function resolveNightMapKey(
  nightKey: string,
  availableNights: string[],
  nightsMap: Record<string, ReportNightEntry>,
): string | null {
  if (nightsMap[nightKey]) {
    return nightKey;
  }

  const nightIndex = availableNights.indexOf(nightKey);
  if (nightIndex >= 0) {
    const indexKey = String(nightIndex);
    if (nightsMap[indexKey]) {
      return indexKey;
    }
  }

  return null;
}

function sortSelectedNightKeys(
  nightKeys: string[],
  availableNights: string[],
): string[] {
  return [...nightKeys].sort((left, right) => {
    const leftIndex = availableNights.indexOf(left);
    const rightIndex = availableNights.indexOf(right);

    if (leftIndex >= 0 && rightIndex >= 0) {
      return leftIndex - rightIndex;
    }
    if (leftIndex >= 0) {
      return -1;
    }
    if (rightIndex >= 0) {
      return 1;
    }

    return left.localeCompare(right, undefined, { numeric: true });
  });
}

function isBetterPricedCell(
  candidate: ResolvedReportCell,
  current: ResolvedReportCell | null,
): boolean {
  if (!current) {
    return true;
  }

  const candidatePrice = candidate.price!;
  const currentPrice = current.price!;

  if (candidatePrice < currentPrice) {
    return true;
  }
  if (candidatePrice > currentPrice) {
    return false;
  }

  const candidateHasDelta = candidate.delta.length > 0;
  const currentHasDelta = current.delta.length > 0;
  if (candidateHasDelta && !currentHasDelta) {
    return true;
  }

  return false;
}

function isBetterEmptyCell(
  candidate: ResolvedReportCell,
  current: ResolvedReportCell | null,
): boolean {
  if (!current) {
    return true;
  }

  const candidateHasDelta = candidate.delta.length > 0;
  const currentHasDelta = current.delta.length > 0;
  if (candidateHasDelta && !currentHasDelta) {
    return true;
  }

  return false;
}

function withPriceDeltaBackground(
  cell: ResolvedReportCell,
  tezMinPrice: number,
): ResolvedReportCell {
  if (cell.hasApiBackground || cell.price === null) {
    return cell;
  }

  const backgroundColor = getPriceDeltaCellBackground(tezMinPrice, cell.price);
  if (!backgroundColor) {
    return cell;
  }

  return {
    ...cell,
    backgroundColor,
  };
}

export function resolveOperatorCell(
  operator: IGenerateReportOperator | undefined,
  selectedNightKeys: string[],
  availableNights: string[] = [],
  options: ResolveOperatorCellOptions = {},
): ResolvedReportCell | null {
  if (!operator) {
    return null;
  }

  const nightsMap = getOperatorNightsMap(operator);
  if (!nightsMap) {
    return null;
  }

  const nightKeys = sortSelectedNightKeys(
    selectedNightKeys.length > 0 ? selectedNightKeys : ["0"],
    availableNights,
  );
  let best: ResolvedReportCell | null = null;
  let bestEmpty: ResolvedReportCell | null = null;

  for (const nightKey of nightKeys) {
    const mapKey = resolveNightMapKey(nightKey, availableNights, nightsMap);
    if (!mapKey) {
      continue;
    }

    const entry = nightsMap[mapKey];
    const parsed = parseNightEntry(entry);
    if (!parsed) {
      continue;
    }

    if (parsed.price !== null) {
      if (isBetterPricedCell(parsed, best)) {
        best = parsed;
      }
    } else if (isBetterEmptyCell(parsed, bestEmpty)) {
      bestEmpty = parsed;
    }
  }

  const resolved = best ?? bestEmpty;
  if (
    !resolved ||
    !options.applyPriceDeltaBackground ||
    options.tezMinPrice == null
  ) {
    return resolved;
  }

  return withPriceDeltaBackground(resolved, options.tezMinPrice);
}

export function getOperatorNamesFromStats(
  stats: { operators: Record<string, IGenerateReportOperator> }[],
): string[] {
  const firstRow = stats[0];
  if (!firstRow) {
    return [];
  }

  return Object.keys(firstRow.operators);
}
